/**
 * The retained-document viewer: rendered page images or the prepared Markdown
 * for one document, opened from the inventory or a citation. The document and
 * page live in the route search (`document`, `page`) so a citation is a link;
 * the image/Markdown choice is local to the open viewer. A cited page that is
 * not retained is reported, never redirected to another page.
 */
import { useEffect, useState } from 'react'
import { IconChevronLeft, IconChevronRight, IconDownload, IconFileText, IconPhoto } from '@tabler/icons-react'
import { safeDocumentUrl } from '../../../api/legal'
import { Alert, Dialog, IconButton, Segmented, Select, Skeletons } from '../../../ui/kit'
import { MarkdownDocument } from './MarkdownDocument'
import { markdownForLegalPage, type LegalDocumentLink } from './legalModel'

export type ViewerMode = 'pages' | 'markdown'

export function LegalDocumentViewer({ open, document, page, mode, onNavigate, onModeChange, onClose }: {
  open: boolean
  document: LegalDocumentLink | null
  page: number | null
  mode: ViewerMode
  onNavigate: (page: number | null) => void
  onModeChange: (mode: ViewerMode) => void
  onClose: () => void
}) {
  const pages = document?.preparedAssets?.pages ?? []
  const sourceUrl = safeDocumentUrl(document?.sourceUrl)
  const active = page === null ? pages[0] : pages.find(candidate => candidate.page === page)
  const [loaded, setLoaded] = useState<{ url: string; text?: string; error?: string } | null>(null)
  const markdownUrl = document?.preparedAssets?.markdownUrl ?? null
  const markdownBaseUrl = markdownUrl ? markdownUrl.slice(0, markdownUrl.lastIndexOf('/') + 1) : undefined
  const markdown = loaded?.url === markdownUrl ? loaded?.text ?? null : null
  const error = loaded?.url === markdownUrl ? loaded?.error : null
  const pageMarkdown = markdown === null || !document ? null : page === null ? markdown : markdownForLegalPage(markdown, document.id, page)

  useEffect(() => {
    if (!open || !markdownUrl || mode !== 'markdown') return
    const controller = new AbortController()
    void fetch(markdownUrl, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/markdown' } })
      .then(async response => { if (!response.ok) throw new Error(`Markdown unavailable (HTTP ${response.status})`); return response.text() })
      .then(text => { if (!controller.signal.aborted) setLoaded({ url: markdownUrl, text }) })
      .catch((cause: unknown) => { if (!controller.signal.aborted) setLoaded({ url: markdownUrl, error: cause instanceof Error ? cause.message : 'Markdown preview failed' }) })
    return () => controller.abort()
  }, [open, markdownUrl, mode])

  useEffect(() => {
    if (!open || !active) return
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLElement && (event.target.closest('input,select,textarea,[role="combobox"]') || event.target.isContentEditable)) return
      const index = pages.findIndex(candidate => candidate.page === active.page)
      const next = event.key === 'ArrowLeft' ? pages[index - 1] : event.key === 'ArrowRight' ? pages[index + 1] : null
      if (next) { event.preventDefault(); onNavigate(next.page) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, active, pages, onNavigate])

  const index = active ? pages.indexOf(active) : -1
  return <Dialog open={open} onClose={onClose} size="full" title={document?.documentName ?? 'Legal-pack source'} className="lg-viewer">
    {!document ? <div className="lg-viewer-pad"><Alert warn tight title="Cited document unavailable">This retained document is not available in this property's pack inventory. No different document has been substituted.</Alert></div> : <>
      <div className="lg-viewer-bar">
        <div className="form-row">
          <Segmented label="Document preview mode" small value={mode} onChange={onModeChange} options={[{ value: 'pages', label: 'Page image' }, { value: 'markdown', label: 'Markdown' }]} />
          <span className="lg-viewer-page"><Select ariaLabel="Document page" value={page === null ? 'all' : String(page)} onChange={value => { if (value !== null) onNavigate(value === 'all' ? null : Number(value)) }}
            options={[{ value: 'all', label: 'Document overview' }, ...pages.map(candidate => ({ value: String(candidate.page), label: `Page ${candidate.page} of ${pages.length}` }))]} /></span>
        </div>
        <div className="form-row">
          {sourceUrl ? <a className="btn is-small" href={sourceUrl} download><IconDownload className="ic-sm" aria-hidden="true" />Original</a> : null}
          {markdownUrl ? <a className="btn is-small" href={markdownUrl} download><IconFileText className="ic-sm" aria-hidden="true" />Markdown</a> : null}
          {active ? <a className="btn is-small" href={active.imageUrl} download><IconPhoto className="ic-sm" aria-hidden="true" />Page image</a> : null}
        </div>
      </div>
      <div className="lg-viewer-pad">
        {page !== null && !active ? <Alert warn tight title={`Page ${page} is unavailable`}>The citation has not been redirected to another page. Choose a retained page above or download the original.</Alert> : null}
        {mode === 'pages' ? active ? <>
          <p className="lg-faint">Source page {active.page} of {pages.length} · page numbers refer to the rendered document, not printed folios.</p>
          <div className="lg-viewer-stage">
            <img className="lg-viewer-image" src={active.imageUrl} alt={`${document.documentName ?? 'Document'} page ${active.page}`} decoding="async" draggable={false} />
            <IconButton className="lg-viewer-prev" label="Previous document page" disabled={index <= 0} onClick={() => { const previous = pages[index - 1]; if (previous) onNavigate(previous.page) }}><IconChevronLeft className="ic" /></IconButton>
            <IconButton className="lg-viewer-next" label="Next document page" disabled={index < 0 || index >= pages.length - 1} onClick={() => { const next = pages[index + 1]; if (next) onNavigate(next.page) }}><IconChevronRight className="ic" /></IconButton>
          </div>
        </> : <p className="t-muted">No prepared page image is available.</p> : <div className="lg-viewer-md">
          {!markdownUrl ? <Alert warn tight title="Markdown unavailable">Prepared Markdown is not available for this retained document.</Alert>
            : error ? <Alert tight title="Markdown preview failed">{error}</Alert>
              : markdown === null ? <Skeletons count={1} kind="block" />
                : pageMarkdown === null ? <Alert warn tight title="No page marker">No matching document/page marker was found. Choose Document overview to read the full Markdown.</Alert>
                  : <MarkdownDocument source={pageMarkdown} baseUrl={markdownBaseUrl} omitFirstHeading="Prepared legal-pack document" />}
        </div>}
      </div>
    </>}
  </Dialog>
}
